import {
  updateUpgradeTemplate,
  useGetUpgradeTemplate,
  useListSubscriptions,
  useListUpgradeTemplateStages,
} from 'api/frontend';
import { Breadcrumbs, SkeletonText } from 'subframe/index';
import { ConsoleLoader } from 'components/Loader';
import MessageBox from 'components/MessageBox';
import { Stack } from 'components/utils';
import Page from 'components/Page';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import { useNavigate, useParams } from 'react-router';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSnackbar } from 'notistack';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import { useSWRConfig } from 'swr';
import {
  isExampleData,
  UPGRADE_STAGES,
  UPGRADE_TEMPLATES,
} from 'src/data/upgrades';
import { UpgradesViewComponent } from 'src/pages/upgrades/UpgradesView';
import styles from 'src/pages/upgrades/UpgradesView.module.scss';
import {
  showArchive,
  showCancel,
  UpgradeViewMode,
} from '../helpers/UpgradesHelpers';
import classNames from 'classnames';
import useAnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import * as Sentry from '@sentry/browser';
import OwnerSelector from '../components/OwnerSelector';
import { ChangeNameDialog } from '../helpers/ChangeNameDialog';
import UpgradesCancelDialog from '../helpers/CancelEntryDialog';
import { useState } from 'react';
import { UpgradeTemplate } from 'api/models';
import RegenerateTemplateDialog from '../components/RegenerateTemplateDialog';
import MarkUpgradeTemplateAsEnvironmentUpgraded from '../components/MarkUpgradeTemplateAsEnvironmentUpgraded';
import MarkUpgradeTemplateAsApproved from '../components/MarkUpgradeTemplateAsApproved';
import { Actions } from 'components/design-system';
import { getTooltipForUpgradeCompletedBy } from '../helpers/UpgradesHelpers';
import { AtomicTooltip } from 'components/design-system';
import { openUpgradeExportInNewWindow } from '../helpers/export_full';
import { useExampleData } from 'hooks/useExampleData';
import MarkUpgradeTemplateAsArchived from '../components/MarkUpgradeTemplateAsArchived';

export default function ClusterUpgradesTemplateView() {
  const { templateId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { logEvent } = useAnalyticsEventLogger();

  const { account, currentOrganization } = useUserAccountState();
  const basePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/templates/clusters',
  );
  const clusterUpgradePlansBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/plans/clusters',
  );
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();

  // Check if the currently open Upgrade Template is an example or real
  const exampleData = templateId !== undefined && isExampleData(templateId);
  const [showExampleData, setShowExampleData] = useExampleData();

  // update view in localStorage if it doesn't match the currently open Upgrade Template
  if (exampleData !== showExampleData) {
    setShowExampleData(exampleData);
  }

  const {
    data: upgradeTemplateData,
    error: upgradeTemplateError,
    mutate: mutateUpgradeTemplate,
  } = useGetUpgradeTemplate(templateId || '', {
    request: { headers: { Authorization: `Bearer ${account.token}` } },
    swr: {
      enabled: !exampleData,
    },
  });
  const { data: upgradeTemplateStagesData, error: upgradeTemplateStagesError } =
    useListUpgradeTemplateStages(
      templateId || '',
      { page_size: 100 },
      {
        request: { headers: { Authorization: `Bearer ${account.token}` } },
        swr: {
          enabled: !exampleData,
        },
      },
    );

  const { data: Subscriptions } = useListSubscriptions(
    currentOrganization?.slug,
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
  );
  const showUpgradePlanLock = Subscriptions
    ? !Subscriptions?.total_entitlement?.features?.includes(
        'request_upgrade_plan',
      ) &&
      !Subscriptions?.total_entitlement?.features?.includes(
        'request_addon_upgrade_plan',
      )
    : true;
  const [openEditNameDialog, setOpenEditNameDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [markAsApprovedDialog, setMarkAsApprovedDialog] = useState(false);
  const [markAsEnvironmentUpgradedDialog, setMarkAsEnvironmentUpgradedDialog] =
    useState(false);
  const [regenerateTemplateDialog, setRegenerateTemplateDialog] =
    useState(false);
  const [
    openMarkUpgradeTemplateAsArchivedDialog,
    setOpenMarkUpgradeTemplateAsArchivedDialog,
  ] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<UpgradeTemplate>();

  //redirect to the upgrade templates page if there is an error in displaying stage data
  if (upgradeTemplateData && upgradeTemplateStagesError) {
    navigate(basePath);
    if (
      upgradeTemplateStagesError?.response?.status === 404 &&
      upgradeTemplateData?.status === 'cancelled'
    ) {
      enqueueSnackbar('No data available for the canceled Upgrade Template', {
        variant: 'warning',
        autoHideDuration: toastAutoHideDuration,
      });
    } else if (
      upgradeTemplateStagesError?.response?.status === 400 &&
      upgradeTemplateData?.status === 'pending'
    ) {
      enqueueSnackbar(
        "This Upgrade Template is being generated. You'll be notified when it's published",
        {
          variant: 'warning',
          autoHideDuration: toastAutoHideDuration,
        },
      );
    } else {
      enqueueSnackbar('No data available for the Upgrade Template', {
        variant: 'error',
        autoHideDuration: toastAutoHideDuration,
      });
    }
  }

  const upgradeTemplate = exampleData
    ? UPGRADE_TEMPLATES.find((u) => u.id === templateId)
    : upgradeTemplateData;
  const upgradeTemplateStages = exampleData
    ? UPGRADE_STAGES.get(templateId || '')
    : upgradeTemplateStagesData?.data;

  const clusterResourcesCount = (upgradeTemplate?.resources?.clusters || [])
    .length;
  if (!clusterResourcesCount) {
    const updatedPath = window.location.pathname.replace('clusters', 'addons');
    navigate(updatedPath);
  }
  const markdownExports = (upgradeTemplate?.exports || []).filter(
    (e) => e.format === 'markdown',
  );

  if (upgradeTemplate === undefined || upgradeTemplateStages === undefined) {
    return (
      <Page title={'Upgrade Template Details'}>
        <div
          className={styles['pageContents']}
          style={{ marginLeft: '32px', width: 'calc(100% - 64px)' }}
        >
          <Breadcrumbs>
            <Breadcrumbs.Item onClick={() => navigate(basePath)}>
              Upgrade Copilot
            </Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item onClick={() => navigate(basePath)}>
              Upgrade Templates
            </Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <SkeletonText className="w-[200px]" />
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item active={true}>
              {upgradeTemplate?.name || templateId}
            </Breadcrumbs.Item>
          </Breadcrumbs>
          {upgradeTemplateError || upgradeTemplateStagesError ? (
            <Stack className="w-full h-full justify-center">
              <MessageBox
                mode={'error'}
                message={
                  upgradeTemplateError?.response?.status === 404
                    ? 'Upgrade Template not found'
                    : 'An error occurred while fetching the Upgrade Template'
                }
              />
            </Stack>
          ) : (
            <ConsoleLoader />
          )}
        </div>
      </Page>
    );
  } else {
    const actions = (
      <>
        <Actions
          size="large"
          sections={[
            {
              title: 'Update Properties',
              items: [
                <Actions.Item
                  key="edit-name"
                  title="Edit Name"
                  icon="FeatherEdit2"
                  onClick={() => {
                    setSelectedTemplate(upgradeTemplate);
                    setOpenEditNameDialog(true);
                  }}
                  disabled={showExampleData}
                  tooltip={
                    showExampleData ? 'Example templates cannot be updated' : ''
                  }
                />,

                <OwnerSelector
                  key="change-owner"
                  onConfirm={async (newOwner) => {
                    if (showExampleData) {
                      enqueueSnackbar('Example templates cannot be updated', {
                        variant: 'warning',
                        autoHideDuration: toastAutoHideDuration,
                      });
                    } else {
                      try {
                        await updateUpgradeTemplate(
                          upgradeTemplate.id,
                          { owner: { id: newOwner } },
                          {
                            headers: {
                              Authorization: `Bearer ${account.token}`,
                            },
                          },
                        );
                        logEvent('upgrade-templates-clusters-owner-change', {
                          template: upgradeTemplate.id,
                          ownerId: newOwner,
                        });
                        mutateUpgradeTemplate();
                        enqueueSnackbar(
                          'Successfully updated the owner of the Upgrade Template',
                          {
                            variant: 'success',
                            autoHideDuration: toastAutoHideDuration,
                          },
                        );
                      } catch (err) {
                        enqueueSnackbar(
                          somethingWentWrong.replace(
                            '<action>',
                            'updating the owner of the Upgrade Template',
                          ),
                          {
                            variant: 'error',
                            autoHideDuration: toastAutoHideDuration,
                          },
                        );
                        Sentry.captureException(err);
                      }
                    }
                  }}
                />,
              ],
            },
            {
              title: 'Workflow Actions',
              items: [
                <Actions.Item
                  key="instantiate-plan"
                  title="Instantiate Plan"
                  icon="FeatherPlus"
                  link={`${clusterUpgradePlansBasePath}/new?template_id=${templateId}`}
                  disabled={
                    showExampleData || upgradeTemplate.status !== 'approved'
                  }
                  tooltip={
                    showExampleData
                      ? 'Action is not available on example data'
                      : upgradeTemplate.status !== 'approved'
                      ? 'Action is only available for Approved Upgrade Templates'
                      : showUpgradePlanLock
                      ? 'Action not available for your current subscription. Upgrade to Business plan.'
                      : ''
                  }
                  subscriptionLock={showUpgradePlanLock}
                />,
                <Actions.Item
                  key="preverify"
                  title="Preverify"
                  icon="FeatherShieldCheck"
                  disabled={true}
                  preview={true}
                />,
              ],
            },
            {
              title: 'Lifecycle Actions',
              items: [
                <Actions.Item
                  key="mark-approved"
                  title="Mark as Approved"
                  icon="FeatherVerified"
                  onClick={() => {
                    setSelectedTemplate(upgradeTemplate);
                    setMarkAsApprovedDialog(true);
                  }}
                  disabled={
                    showExampleData || upgradeTemplate.status !== 'in-progress'
                  }
                  tooltip={
                    showExampleData
                      ? 'Example templates cannot be updated'
                      : upgradeTemplate.status !== 'in-progress'
                      ? 'Action is only available for Available Upgrade Templates'
                      : ''
                  }
                />,
                <Actions.Item
                  key="mark-environment-upgraded"
                  title="Mark as Environment Upgraded"
                  icon="FeatherCheck"
                  onClick={() => {
                    setSelectedTemplate(upgradeTemplate);
                    setMarkAsEnvironmentUpgradedDialog(true);
                  }}
                  disabled={
                    showExampleData || upgradeTemplate.status !== 'approved'
                  }
                  tooltip={
                    showExampleData
                      ? 'Example templates cannot be updated'
                      : upgradeTemplate.status !== 'approved'
                      ? 'Action is only available for Approved Upgrade Templates'
                      : ''
                  }
                />,
                <Actions.Item
                  key="regenerate-template"
                  title="Regenerate Template"
                  icon="FeatherRefreshCcw"
                  onClick={() => {
                    setSelectedTemplate(upgradeTemplate);
                    setRegenerateTemplateDialog(true);
                  }}
                  disabled={
                    showExampleData ||
                    !upgradeTemplate.is_regenerate_pending ||
                    upgradeTemplate.status !== 'in-progress'
                  }
                  tooltip={
                    showExampleData
                      ? 'Action is not available on example data'
                      : upgradeTemplate.status !== 'in-progress'
                      ? 'Action is only available for Available Upgrade Templates'
                      : !upgradeTemplate.is_regenerate_pending
                      ? 'No pending feedback for regeneration'
                      : ''
                  }
                />,
                <Actions.Item
                  key="cancel-template"
                  title="Cancel Template"
                  icon="FeatherX"
                  onClick={() => {
                    setSelectedTemplate(upgradeTemplate);
                    setOpenCancelDialog(true);
                  }}
                  disabled={showExampleData || !showCancel(upgradeTemplate)}
                  tooltip={
                    showExampleData
                      ? 'Example templates cannot be canceled'
                      : !showCancel(upgradeTemplate)
                      ? 'Action is not available for this Upgrade Template'
                      : ''
                  }
                />,
                <Actions.Item
                  key="mark-archived"
                  title="Archive"
                  icon="FeatherArchive"
                  onClick={() => {
                    setOpenMarkUpgradeTemplateAsArchivedDialog(true);
                  }}
                  disabled={showExampleData || !showArchive(upgradeTemplate)}
                  tooltip={
                    showExampleData
                      ? 'Action is not available on example data'
                      : !showArchive(upgradeTemplate)
                      ? 'Action is not available for this Upgrade Template'
                      : ''
                  }
                />,
              ],
            },
            {
              title: 'Export',
              items: [
                <Actions.Item
                  key="export-as-markdown"
                  title="Export as Markdown"
                  icon="FeatherCode2"
                  onClick={() => {
                    openUpgradeExportInNewWindow(
                      UpgradeViewMode.Template,
                      upgradeTemplate.id,
                      account.token,
                      enqueueSnackbar,
                    );
                  }}
                  disabled={showExampleData || markdownExports.length < 1}
                  tooltip={
                    showExampleData
                      ? 'Example Upgrade Templates cannot be exported'
                      : markdownExports.length < 1
                      ? 'Action is not available for this Upgrade Template'
                      : ''
                  }
                />,
              ],
            },
          ]}
        />
        <ChangeNameDialog
          key="edit-name"
          open={openEditNameDialog}
          setOpen={setOpenEditNameDialog}
          mode={UpgradeViewMode.Template}
          name={selectedTemplate?.name || ''}
          onConfirm={async (newName) => {
            if (showExampleData) {
              enqueueSnackbar('Example templates cannot be updated', {
                variant: 'warning',
                autoHideDuration: toastAutoHideDuration,
              });
            } else {
              try {
                await updateUpgradeTemplate(
                  selectedTemplate?.id || '',
                  { name: newName },
                  {
                    headers: {
                      Authorization: `Bearer ${account.token}`,
                    },
                  },
                );
                logEvent('upgrade-templates-clusters-name-change', {
                  template: selectedTemplate?.id || '',
                  name: newName,
                });
                mutateUpgradeTemplate();
                enqueueSnackbar(
                  'Successfully edited the requested properties of the Upgrade Template',
                  {
                    variant: 'success',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
              } catch (err) {
                enqueueSnackbar(
                  somethingWentWrong.replace(
                    '<action>',
                    'updating requested properties of the Upgrade Template',
                  ),
                  {
                    variant: 'error',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
                Sentry.captureException(err);
              }
            }
          }}
        />
        <UpgradesCancelDialog
          open={openCancelDialog}
          setOpen={setOpenCancelDialog}
          mode={UpgradeViewMode.Template}
          onConfirm={async () => {
            if (showExampleData) {
              enqueueSnackbar('Example templates cannot be canceled', {
                variant: 'warning',
                autoHideDuration: toastAutoHideDuration,
              });
            } else {
              try {
                await updateUpgradeTemplate(
                  selectedTemplate?.id || '',
                  { status: 'cancelled' },
                  {
                    headers: {
                      Authorization: `Bearer ${account.token}`,
                    },
                  },
                );
                logEvent('upgrade-templates-clusters-cancel', {
                  template: selectedTemplate?.id || '',
                  name: selectedTemplate?.name || '',
                });
                mutateUpgradeTemplate();
                enqueueSnackbar('Successfully canceled Upgrade Template', {
                  variant: 'success',
                  autoHideDuration: toastAutoHideDuration,
                });
              } catch (err) {
                enqueueSnackbar(
                  somethingWentWrong.replace(
                    '<action>',
                    'canceling this Upgrade Template',
                  ),
                  {
                    variant: 'error',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
                Sentry.captureException(err);
              }
            }
          }}
        />
        <MarkUpgradeTemplateAsApproved
          upgradeId={selectedTemplate?.id || ''}
          mutateTemplate={() => mutateUpgradeTemplate()}
          templateOwnerId={selectedTemplate?.owner?.id || ''}
          open={markAsApprovedDialog}
          onClose={() => setMarkAsApprovedDialog(false)}
        />
        <MarkUpgradeTemplateAsEnvironmentUpgraded
          templateId={selectedTemplate?.id || ''}
          mutateTemplate={() => mutateUpgradeTemplate()}
          open={markAsEnvironmentUpgradedDialog}
          onClose={() => setMarkAsEnvironmentUpgradedDialog(false)}
        />
        <RegenerateTemplateDialog
          upgrade={selectedTemplate!}
          open={regenerateTemplateDialog}
          onClose={() => setRegenerateTemplateDialog(false)}
          onConfirm={() => mutateUpgradeTemplate()}
          account={account}
        />
        <MarkUpgradeTemplateAsArchived
          templateId={upgradeTemplate.id}
          mutateTemplate={() => {}}
          open={openMarkUpgradeTemplateAsArchivedDialog}
          onClose={() => setOpenMarkUpgradeTemplateAsArchivedDialog(false)}
        />
      </>
    );
    return (
      <UpgradesViewComponent
        actions={actions}
        mode={UpgradeViewMode.Template}
        account={account}
        upgradePlan={upgradeTemplate}
        stages={upgradeTemplateStages}
        navigate={navigate}
        basePath={basePath}
        stagesStepsDefaultOpen={false}
        additionalHeaderElements={
          <>
            {upgradeTemplate.owner && (
              <div className={styles['stack-18c91f9b']}>
                <span className={styles['text']}>Owner:</span>

                <span className={classNames(styles['text'], 'ph-no-capture')}>
                  {upgradeTemplate.owner?.name ||
                    upgradeTemplate.owner?.id ||
                    ''}
                </span>
              </div>
            )}
            {upgradeTemplate.completed_by && upgradeTemplate.updated && (
              <div className={styles['stack-18c91f9b']}>
                <span
                  className={styles['text']}
                >{`Marked Environment Upgraded by:`}</span>
                <AtomicTooltip
                  tooltipContent={getTooltipForUpgradeCompletedBy(
                    upgradeTemplate.completed_by?.name,
                    upgradeTemplate.updated,
                  )}
                  className="ph-no-capture"
                >
                  <span className={classNames(styles['text'], 'ph-no-capture')}>
                    {upgradeTemplate.completed_by?.name || ''}
                  </span>
                </AtomicTooltip>
              </div>
            )}
          </>
        }
        breadcrumb={'Clusters'}
        onChange={(mutateStage = true, mutatePlan = false) => {
          mutatePlan && mutateUpgradeTemplate();
          mutateStage &&
            globalMutate((key) => {
              if (Array.isArray(key)) {
                if (
                  (key[0] as string).startsWith(
                    `/upgrade_templates/${upgradeTemplate.id}/`,
                  )
                ) {
                  return true;
                }
              }
              return false;
            });
        }}
      />
    );
  }
}
